import React, { forwardRef, useState } from 'react';
import { Upload, X, FileText } from 'lucide-react';

const UploadFile = forwardRef(({
    label,
    error,
    className = '',
    id,
    onChange,
    accept,
    ...props
}, ref) => {
    const [fileName, setFileName] = useState('');

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
        } else {
            setFileName('');
        }

        if (onChange) {
            onChange(e);
        }
    };

    const handleClear = (e) => {
        e.preventDefault();
        setFileName('');
        // Reset the input value
        const input = document.getElementById(id);
        if (input) input.value = '';

        // Trigger generic onChange with empty value if needed, 
        // though typically react-hook-form handles this via register
    };

    return (
        <div className="flex flex-col gap-1.5">
            {label && (
                <label
                    htmlFor={id}
                    className="text-sm font-medium text-gray-700"
                >
                    {label}
                </label>
            )}

            <div className="relative">
                <input
                    ref={ref}
                    id={id}
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept={accept}
                    {...props}
                />

                {!fileName ? (
                    <label
                        htmlFor={id}
                        className={`
                            flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors
                            ${error ? 'border-red-500 bg-red-50' : ''}
                            ${className}
                        `}
                    >
                        <div className="flex flex-col items-center justify-center pb-6 pt-5">
                            <Upload className="mb-2 h-8 w-8 text-gray-400" />
                            <p className="mb-2 text-sm text-gray-500">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">
                                {accept ? accept.split(',').join(', ') : 'Any file'}
                            </p>
                        </div>
                    </label>
                ) : (
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className="p-2 bg-blue-50 rounded-lg">
                                <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                                {fileName}
                            </span>
                        </div>
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
                            type="button"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <span className="text-sm text-red-500">{error.message}</span>
            )}
        </div>
    );
});

UploadFile.displayName = "UploadFile";

export default UploadFile;